/**
 * Security headers — runtime CSP injection.
 *
 * Why this lives in the SPA rather than in nginx config:
 *   - The CSP must reference the **actual** `apiBaseUrl` and
 *     `wsBaseUrl` hostnames from the runtime-config (not the
 *     nginx template's static value), otherwise we can't ship
 *     one container image to multiple environments.
 *   - The security headers only need to apply to the SPA itself,
 *     not to `/healthz` or `/config/runtime-config.json`.
 *
 * Meta-tag caveats (browser-enforced, not server-enforced):
 *   - `frame-ancestors` is IGNORED when delivered via `<meta>` —
 *     it MUST be an HTTP response header. We rely on the nginx
 *     header for this.
 *   - `X-Frame-Options` is similarly ignored via `<meta>` and
 *     relies on the nginx header.
 *   - `Strict-Transport-Security` and `Permissions-Policy` are
 *     also ignored via `<meta>` and rely on nginx.
 *
 * The CSP `connect-src` directive IS honoured via `<meta>`, so we
 * set it dynamically once the runtime-config has loaded (we need
 * the api/ws hosts). The remaining static headers are set
 * by nginx; we mirror them as meta tags only where the spec
 * permits (Referrer-Policy, X-Content-Type-Options).
 */

import { getOptionalRuntimeConfig } from './runtime-config';

declare const __APP_VERSION__: string;
declare const __APP_BUILD_TIME__: string;

/**
 * Headers we set via meta tags. Excludes `frame-ancestors`,
 * `X-Frame-Options`, `Strict-Transport-Security`, and
 * `Permissions-Policy` — those are HTTP-header-only and must be
 * applied via nginx (see `infra/k8s/nginx.conf`).
 */
const META_TAG_NAMES = [
  'Content-Security-Policy',
  'X-Content-Type-Options',
  'Referrer-Policy',
] as const;

function setMeta(name: string, content: string): void {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[http-equiv="${name}"]`,
  );
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('http-equiv', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(name: string): void {
  if (typeof document === 'undefined') return;
  const el = document.head.querySelector(`meta[http-equiv="${name}"]`);
  if (el) el.remove();
}

/**
 * Build a CSP that allows the SPA to talk to its own origin and the
 * Zenoh webserver peer (REST + SSE). Inline styles are required
 * because the design-system uses inline `style={{ ... }}` props
 * on a handful of components (ErrorBoundary, AvatarMenu, etc.).
 */
export function buildCsp(): string {
  const cfg = getOptionalRuntimeConfig();
  const apiHost = cfg?.apiBaseUrl ? new URL(cfg.apiBaseUrl).origin : null;
  // wsBaseUrl was removed in the convergence target (plan §6.4):
  // fetch-SSE reuses the API origin via Envoy.
  const zitadelHost = cfg?.zitadel?.issuer
    ? new URL(cfg.zitadel.issuer).origin
    : null;

  // Build connect-src as a deduped list so we don't get repeated
  // 'self' tokens when the api/ws URLs are the same origin as the
  // SPA itself (a common deployment shape). Note: the string in the
  // array must include the LITERAL quotes — CSP `'self'` is a
  // keyword source-list token, not a host name. `["'self'"]` not
  // `["self"]`.
  const connectHosts = ["'self'"];
  if (apiHost && !connectHosts.includes(apiHost)) connectHosts.push(apiHost);
  // The authorization-code + PKCE token exchange (oidc-client-ts's
  // `exchangeCode` -> `postForm` -> `fetch`) is a background XHR/fetch
  // POST to the ZITADEL issuer's token endpoint, NOT a browser
  // `<form>` submission — it's gated by `connect-src`, not
  // `form-action`. Omitting it here silently blocks every real
  // (authMode=zitadel) login's token exchange; authMode=mock never
  // makes this network call, which is why this went unnoticed.
  if (zitadelHost && !connectHosts.includes(zitadelHost)) connectHosts.push(zitadelHost);

  const formHosts = ["'self'"];
  if (zitadelHost && !formHosts.includes(zitadelHost)) formHosts.push(zitadelHost);

  return [
    `default-src 'self'`,
    `script-src 'self' 'unsafe-inline'`,
    // Inline styles are required by the design system and the
    // canvas-rendered map icons (drawing fills via setAttribute).
    // Google Fonts stylesheet host matches nginx.conf's static CSP
    // (infra/k8s/nginx.conf) — the two are INTERSECTED by the
    // browser, so this list must be a superset-compatible match,
    // not just "whatever this page happens to need".
    `style-src 'self' 'unsafe-inline' https://fonts.googleapis.com`,
    // base64-encoded floorplan PNGs live in the runtime config;
    // canvas-toBlob uses blob: URLs.
    `img-src 'self' data: blob:`,
    `font-src 'self' data: https://fonts.gstatic.com`,
    // connect-src gates fetch / EventSource (Phase 0 SPA uses
    // EventSource for the single broad SSE subscription).
    `connect-src ${connectHosts.join(' ')}`,
    // No `worker-src 'self' blob:` — mqtt.js's worker was removed
    // in Phase 0; if Phase 1 adds a service worker it'll need its
    // own directive.
    `worker-src 'self'`,
    // Don't allow the SPA to be embedded as a `<base>` target.
    `base-uri 'self'`,
    // The OIDC form-post redirect goes to the ZITADEL issuer; allow it.
    `form-action ${formHosts.join(' ')}`,
    // Don't allow legacy plugin content (Flash, Java, etc.).
    `object-src 'none'`,
    // Upgrade insecure requests when on HTTPS-capable browsers.
    `upgrade-insecure-requests`,
  ].join('; ');
}

/**
 * Apply every security header to `<meta http-equiv>` tags. Only
 * headers that the browser actually accepts via `<meta>` are
 * written here; the rest are enforced by nginx.
 */
export function applySecurityHeaders(): void {
  if (typeof document === 'undefined') return;
  setMeta('Content-Security-Policy', buildCsp());
  setMeta('X-Content-Type-Options', 'nosniff');
  setMeta('Referrer-Policy', 'strict-origin-when-cross-origin');

  // Defensive cleanup: if a previous build left any ignored
  // directives in the DOM, drop them so they don't keep
  // generating console warnings.
  removeMeta('X-Frame-Options');
  removeMeta('Strict-Transport-Security');
  removeMeta('Permissions-Policy');
}

/**
 * Build the JSON payload for `/__healthz.json`. Surfaced by
 * `useHealthzRoute`; not exported from `main.tsx` directly.
 */
export interface HealthzPayload {
  status: 'ok' | 'degraded' | 'broken';
  version: string;
  builtAt: string;
  config: {
    loaded: boolean;
    valid: boolean;
    env: 'local' | 'staging' | 'production' | 'unknown';
    authMode: 'zitadel' | 'mock' | 'unknown';
  };
}

export function buildHealthzPayload(): HealthzPayload {
  const cfg = getOptionalRuntimeConfig();
  return {
    status: cfg ? 'ok' : 'broken',
    version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
    builtAt: typeof __APP_BUILD_TIME__ !== 'undefined' ? __APP_BUILD_TIME__ : new Date().toISOString(),
    config: {
      loaded: cfg !== null,
      valid: cfg !== null,
      env: cfg?.env ?? 'unknown',
      authMode: cfg?.authMode ?? 'unknown',
    },
  };
}

/** Internal: list of meta tags we manage, exported for tests. */
export const __META_TAG_NAMES = META_TAG_NAMES;
